import React from 'react';

import { usePlatesByType } from 'client/app/api/PlateTypesApi';
import CANCEL_CHOICE from 'client/app/components/Parameters/cancel';
import ExistingPlateSelect, {
  ExistingPlateSelectProps,
} from 'client/app/components/Parameters/PlateType/ExistingPlateSelect';
import { PlateAndCarrier } from 'client/app/components/Parameters/PlateType/PlateAndCarrier';
import { PlateMode } from 'client/app/components/Parameters/PlateType/PlatesGrid';
import {
  PlateTypeSelect,
  PlateTypeSelectProps,
} from 'client/app/components/Parameters/PlateType/PlateTypeSelect';
import { PlateParameterValue } from 'client/app/components/Parameters/PlateType/processPlateParameterValue';
import makeStylesHook from 'common/ui/hooks/makeStylesHook';

type PlateSelectorConfig =
  | React.ComponentType<PlateTypeSelectProps>
  | {
      Component: typeof PlateTypeSelect;
      additionalProps?: Pick<PlateTypeSelectProps, 'plateMode'>;
    }
  | {
      Component: typeof ExistingPlateSelect;
      additionalProps?: Pick<ExistingPlateSelectProps, 'value' | 'displayValue'>;
    };

type Props = {
  value?: PlateParameterValue;
  isDisabled?: boolean;
  /**
   * plateMode enables restriction of the selectable plates to those compatible
   * with a devices input or output positions for __already selected__ plates.
   *
   * @default both - input and output position plates are selectable
   */
  plateMode?: PlateMode;
  /**
   * plateSelectors enables customization of the editor(s) used to select a
   * __new__ plate.
   *
   * @default [PlateTypeSelect] - simple plate type selection is enabled
   */
  plateSelectors?: PlateSelectorConfig[];
  onChange: (value?: PlateParameterValue) => void;
};

export type PlateSelection = string | null | undefined | typeof CANCEL_CHOICE;

export default function PlateSelectionEditor({
  onChange,
  value,
  isDisabled,
  plateMode = 'both',
  plateSelectors = [PlateTypeSelect],
}: Props) {
  const classes = useStyles();

  const [platesByType] = usePlatesByType();

  const plateSelectorsToRender = plateSelectors.map((selector, index) => {
    if (typeof selector === 'function') {
      const Component = selector;
      return <Component key={index} isDisabled={isDisabled} onChange={onChange} />;
    }
    const { Component, additionalProps } = selector;
    return (
      <Component
        key={index}
        isDisabled={isDisabled}
        onChange={onChange}
        {...additionalProps}
      />
    );
  });

  return (
    <>
      {!value ? (
        <div className={classes.btnsContainer}>{plateSelectorsToRender}</div>
      ) : (
        <PlateAndCarrier
          isDisabled={isDisabled}
          onChange={onChange}
          value={value}
          platesByType={platesByType}
          plateMode={plateMode}
        />
      )}
    </>
  );
}

const useStyles = makeStylesHook(() => ({
  btnsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
}));
